import { Button, Form, Input } from 'antd-mobile-v5'
import { FormattedMessage, useIntl } from 'react-intl'
import React, { useEffect, useState } from 'react'
import { getParameterByName, showAlert } from '../../components/common/utils'

import { useWeWorkMobileBind } from '../../services/hooks/user'
import { useDsdImage, useDsdImageByMobile } from '../../services/hooks'
import Loading from '../../components/Loading'

import './DsdWeWorkMobile.css'
import styles from './DsdWeWorkMobile.module.scss'

function DsdWeWorkMobilePage() {
  const [form] = Form.useForm()
  const [isSuccess, setIsSuccess] = useState(false)
  const [pageState, setPageState] = useState('loading') // 'loading' | 'form' | 'pending' | 'certificate'
  const [imageData, setImageData] = useState(null)
  const { formatMessage } = useIntl()

  // Get externalUserId from URL parameter (support both 'external' and 'exter')
  const externalUserId = getParameterByName('external') || getParameterByName('exter')

  // Hook to check certificate status by external ID
  const { loading: imageLoading, run: getCertificate } = useDsdImage(externalUserId, {
    onSuccess: (data) => {
      // Check for certificate image in different possible fields
      const imageUrl = data && (data.imgUrl || data.imgFile || data.imageUrl)
      if (imageUrl) {
        // Certificate is ready, show it
        setImageData({ ...data, imgUrl: imageUrl })
        setPageState('certificate')
      } else if (data && data.remarkMobiles) {
        // Mobile number submitted but certificate not ready yet
        setPageState('pending')
      } else {
        // No mobile number submitted yet, show form
        setPageState('form')
      }
    },
    onError: (error) => {
      // Certificate not found or other error, show form
      setPageState('form')
    },
  })

  // Hook to check certificate status by mobile number (fallback)
  const { loading: mobileImageLoading, run: getCertificateByMobile } = useDsdImageByMobile({
    onSuccess: (data) => {
      if (data && data.imgUrl) {
        setImageData(data)
        setPageState('certificate')
      } else {
        showAlert(formatMessage({ id: 'common-search-error' }), formatMessage({ id: 'common-close' }))
      }
    },
    onError: (error) => {
      showAlert(error.message || formatMessage({ id: 'common-search-error' }), formatMessage({ id: 'common-close' }))
    },
  })

  const { loading, run: binding } = useWeWorkMobileBind({
    onSuccess: (data) => {
      if (data.status == 0) {
        setIsSuccess(true)
        setPageState('pending') // After successful binding, show pending state
      } else {
        showAlert(data.msg, formatMessage({ id: 'common-close' }))
      }
    },
    onError: (error, params) => {
      showAlert(error.message, formatMessage({ id: 'common-close' }))
    },
  })

  // Check certificate status on component mount
  useEffect(() => {
    if (externalUserId) {
      getCertificate()
    } else {
      // No external user ID, show form directly
      setPageState('form')
    }
  }, [externalUserId, getCertificate])

  const handleBinding = (values) => {
    let data = values
    data.externalUserId = externalUserId
    binding(data)
  }

  // Function to search certificate by mobile number
  const handleMobileSearch = (values) => {
    getCertificateByMobile(values)
  }

  // Handle different page states
  if (pageState === 'loading' || imageLoading || mobileImageLoading) {
    return <Loading />
  }

  if (pageState === 'certificate' && imageData) {
    return (
      <div className={styles.wrapper}>
        <img src={imageData.imgUrl} alt="certificate" className={styles.bg} />
      </div>
    )
  }

  // Just submitted successfully - show only success message
  if (isSuccess) {
    return (
      <div className="home-page">
        <div className="logo">
          <img src={require('../../assets/images/common/check-success.png')} alt="success" />
        </div>
        <div className={styles.successContainer}>
          <div className={styles.successMessage}>
            <p>{formatMessage({ id: 'dsd-mobile-submit-success-message' })}</p>
          </div>
        </div>
      </div>
    )
  }

  // Previously submitted, certificate still pending - show only pending info
  if (pageState === 'pending') {
    return (
      <div className="home-page">
        <div className="logo">
          <img src={require('../../assets/images/common/PADI-logo.png')} alt="logo" />
        </div>
        <div className={styles.successContainer}>
          <div className={styles.pendingMessage}>
            <p>{formatMessage({ id: 'dsd-certificate-pending-message' })}</p>
          </div>
          <Button
            className={styles.checkButton}
            color="primary"
            onClick={() => getCertificate()}
            loading={imageLoading}
          >
            {formatMessage({ id: 'dsd-check-certificate-button' })}
          </Button>
        </div>
      </div>
    )
  }

  // Form state - show appropriate form based on whether externalUserId exists
  if (pageState === 'form') {
    const isBinding = !!externalUserId // If we have externalUserId, show binding form
    const formTitle = isBinding ? 'bindMobileForWeWork' : 'dsd-image-mobile-form-title'
    const onFinish = isBinding ? handleBinding : handleMobileSearch
    const loadingState = isBinding ? loading : mobileImageLoading

    return (
      <div className="home-page">
        <div className="logo">
          <img src={require('../../assets/images/common/PADI-logo.png')} alt="logo" />
        </div>
        <Form
          form={form}
          onFinish={(values) => onFinish(values)}
          validateTrigger="submit"
          footer={
            <div>
              <Button block type="submit" color="primary" loading={loadingState}>
                {formatMessage({ id: 'common-comfirmBtn' })}
              </Button>
            </div>
          }
        >
          <Form.Header>
            <FormattedMessage id={formTitle} />
          </Form.Header>

          <Form.Item
            label={formatMessage({ id: 'addressForm-mobile' })}
            name="mobile"
            className={styles.formItem}
            rules={[
              {
                required: true,
                message: formatMessage({ id: 'setMobile-mobileInput' }),
              },
              {
                pattern: /^[0-9]*$/,
                message: formatMessage({ id: 'common-errorMobile' }),
              },
            ]}
          >
            <Input placeholder={formatMessage({ id: 'setMobile-mobileInput' })} />
          </Form.Item>
        </Form>
      </div>
    )
  }

  // Default fallback - should not happen, but show loading as fallback
  return <Loading />
}

export default DsdWeWorkMobilePage
